import { Component, Event, EventEmitter, h, Method, Prop } from "@stencil/core"
import LanguageHandler from "@payair/e-com-common-sdk/core/LanguageHandler"
import { reconcileControlledValue } from "../../helpers/reconcileControlledValue"
import { getLanguageHandler, getSupportedLocaleData } from "../../languageHandler/LanguageHandler"
import inputErrorIcon from "../../styles/inputErrorIcon.svg"

let textInputId = 0

/**
 * Reusable controlled text input.
 */
@Component({
  tag: "text-input",
  styleUrl: "TextInput.scss",
  shadow: true,
})
export class TextInput {
  /**
   * Merchant site preferred locale. Format based on ISO format for language (ISO 639-1) and country code
   * (ISO 3166-1 alpha-2). The language and country should be separated using an underscore ( _ ).
   * <br /><br />
   * **Supported locale**: en_GB, sv_SE, pl_PL
   * <br /><br />
   * @default en_GB
   */
  @Prop() locale?: string

  /** Current controlled value. */
  @Prop() value: string = ""

  /** Translation key used for the label. */
  @Prop() labelKey: string

  /** Translation key used for the placeholder. Defaults to labelKey. */
  @Prop() placeholderKey?: string

  /** Translation key used for the validation message. */
  @Prop() errorMessageKey?: string

  /** Fully translated validation message. Takes precedence over errorMessageKey. */
  @Prop() errorMessage?: string

  /** Native input name. */
  @Prop() name?: string

  /** Native autocomplete hint. */
  @Prop() autocomplete?: string

  /** Whether the input is required. */
  @Prop() required?: boolean

  /** Whether the input is disabled. */
  @Prop() disabled?: boolean

  /** Whether the input should be rendered in its invalid state. */
  @Prop() invalid?: boolean

  /** Emitted whenever the user edits the value. */
  @Event() valueChange: EventEmitter<string>

  /** Emitted when focus leaves the input. */
  @Event() fieldBlur: EventEmitter<void>

  /** Emitted when Enter is pressed in the input. */
  @Event() fieldSubmit: EventEmitter<void>

  private languageHandler: LanguageHandler
  private readonly inputId = `text-input-${textInputId++}`
  private inputElement?: HTMLInputElement

  componentWillLoad() {
    const localeData = getSupportedLocaleData(this.locale)
    this.languageHandler = getLanguageHandler(localeData.lang)
  }

  private onInput = (event: InputEvent) => {
    const input = event.target as HTMLInputElement
    this.valueChange.emit(input.value)

    if (!event.isComposing) {
      reconcileControlledValue(input, () => this.value)
    }
  }

  private onCompositionEnd = (event: CompositionEvent) => {
    reconcileControlledValue(event.target as HTMLInputElement, () => this.value)
  }

  private onBlur = () => {
    this.fieldBlur.emit()
  }

  private onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter" && !event.isComposing && !event.defaultPrevented && !event.repeat && !this.disabled) {
      event.preventDefault()
      this.fieldSubmit.emit()
    }
  }

  /** Focuses the native text input. */
  @Method()
  async focusInput() {
    this.inputElement?.focus()
  }

  render() {
    const label = this.languageHandler.getString(this.labelKey)
    const placeholder = this.languageHandler.getString(this.placeholderKey || this.labelKey)
    const errorMessage =
      this.errorMessage || (this.errorMessageKey ? this.languageHandler.getString(this.errorMessageKey) : undefined)

    return (
      <div class="textInput">
        <label class="inputLabel" htmlFor={this.inputId}>
          {label}
        </label>
        <div class="inputContainer">
          <input
            ref={element => (this.inputElement = element)}
            id={this.inputId}
            class="input"
            type="text"
            name={this.name}
            autocomplete={this.autocomplete}
            placeholder={placeholder}
            value={this.value}
            required={this.required}
            disabled={this.disabled}
            aria-invalid={this.invalid ? "true" : "false"}
            aria-describedby={this.invalid && errorMessage ? `${this.inputId}-error` : undefined}
            onInput={this.onInput}
            onCompositionend={this.onCompositionEnd}
            onKeyDown={this.onKeyDown}
            onBlur={this.onBlur}
          />
          {this.invalid && <img class="errorIcon" src={inputErrorIcon} alt="" aria-hidden="true" />}
        </div>
        {this.invalid && errorMessage && (
          <div id={`${this.inputId}-error`} class="errorInfo" role="alert">
            {errorMessage}
          </div>
        )}
      </div>
    )
  }
}
